import { config } from '../constants.js';

const url = config.url.API_URL;

/**
 * Start annual access checkout.
 * Uses Stripe Checkout when BILLING_MODE=stripe, otherwise completes via stub.
 *
 * @param {{ intent?: 'join' | 'renew' }} [options]
 */
export async function startAnnualCheckout(options = {}) {
  const payload = { intent: options.intent || 'join' };
  if (options.returnTo) {
    payload.returnTo = options.returnTo;
  }

  const response = await fetch(`${url}/billing/create-checkout-session`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || `Checkout failed (${response.status})`);
    error.code = data.code;
    error.status = response.status;
    throw error;
  }

  if (data.mode === 'stripe' && data.url) {
    window.location.assign(data.url);
    return {
      redirecting: true,
      mode: 'stripe',
      sessionId: data.sessionId,
    };
  }

  if (data.mode === 'already_active') {
    return {
      redirecting: false,
      mode: 'already_active',
      alreadyActive: true,
      access: data.access,
    };
  }

  return completeStubCheckout(options);
}

/**
 * Start teacher class-seat pack checkout.
 * Uses Stripe Checkout when BILLING_MODE=stripe, otherwise completes via stub.
 *
 * @param {{
 *   extraQuantity?: number,
 *   quantity?: number,
 *   returnTo?: string,
 * }} options
 */
export async function startClassSeatsCheckout(options = {}) {
  const payload = {
    pack: options.pack,
    action: options.action,
    targetSeats: options.targetSeats,
    extraQuantity: options.extraQuantity,
    quantity: options.quantity,
  };
  if (options.returnTo) {
    payload.returnTo = options.returnTo;
  }

  const response = await fetch(`${url}/billing/create-class-seats-checkout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || `Checkout failed (${response.status})`);
    error.code = data.code;
    error.status = response.status;
    throw error;
  }

  if (data.mode === 'stripe' && data.url) {
    window.location.assign(data.url);
    return {
      redirecting: true,
      mode: 'stripe',
      sessionId: data.sessionId,
      quote: data.quote,
    };
  }

  return completeStubClassSeatsCheckout(payload);
}

/**
 * Create a Stripe invoice for a teacher seat pack (or complete immediately in stub mode).
 *
 * @param {{
 *   extraQuantity?: number,
 *   quantity?: number,
 *   invoiceEmail?: string,
 *   poNumber?: string,
 * }} options
 */
export async function startClassSeatsInvoice(options = {}) {
  const payload = {
    pack: options.pack,
    action: options.action,
    targetSeats: options.targetSeats,
    extraQuantity: options.extraQuantity,
    quantity: options.quantity,
    invoiceEmail: options.invoiceEmail,
    poNumber: options.poNumber,
  };

  const response = await fetch(`${url}/billing/create-class-seats-invoice`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || `Invoice failed (${response.status})`);
    error.code = data.code;
    error.status = response.status;
    throw error;
  }

  if (data.mode === 'stripe' && data.url) {
    return {
      redirecting: false,
      mode: 'stripe',
      invoiceId: data.invoiceId,
      quote: data.quote,
      url: data.url,
      invoice: data.invoice,
    };
  }

  return {
    ...data,
    mode: data.mode || 'stub',
    redirecting: false,
  };
}

/**
 * Void an unpaid class-seat invoice.
 */
export async function voidClassSeatsInvoice(stripeInvoiceId) {
  const response = await fetch(`${url}/billing/void-class-seats-invoice`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stripeInvoiceId }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || `Unable to void invoice (${response.status})`);
    error.code = data.code;
    error.status = response.status;
    throw error;
  }

  return data;
}

/**
 * Complete class-seat purchase via billing stub.
 */
export async function completeStubClassSeatsCheckout(payload = {}) {
  const response = await fetch(`${url}/billing/stub/complete-class-seats`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || `Checkout failed (${response.status})`);
    error.code = data.code;
    error.status = response.status;
    throw error;
  }

  return {
    ...data,
    mode: 'stub',
    redirecting: false,
  };
}

/**
 * Complete annual access via billing stub (dev/staging when BILLING_MODE=stub).
 *
 * @param {{ intent?: 'join' | 'renew' }} [options]
 */
export async function completeStubCheckout(options = {}) {
  const response = await fetch(`${url}/billing/stub/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ intent: options.intent || 'join' }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || `Checkout failed (${response.status})`);
    error.code = data.code;
    error.status = response.status;
    throw error;
  }

  return {
    ...data,
    mode: 'stub',
    redirecting: false,
  };
}
